// --- Brand Colors --- (design palette: Polygon Health Trends Redesign, adopted HW-wide 2026-07-13)
export const BRAND = '#efeaff';        // accent tint surface (info boxes, selected chips)
export const BRAND_DARK = '#d7d3f0';   // accent-tinted borders, soft accent icons
export const BRAND_DARKER = '#6c4ce0'; // primary accent (buttons, links, active pills)
export const BG = '#f2f2f6';           // page background

// --- Motion ---
// Slide-in played when the onboarding guided loop ADVANCES to another provider (the org row + the
// Skip/Log in buttons are keyed by the current org, so they REMOUNT per org and this runs).
// Purpose: make the provider CHANGE perceivable — after a Skip the swap was instant and users
// couldn't tell what happened. (Functional animation — unlike the decorative delays we removed.)
// ★ MOVED HERE 2026-10-04 so the "missing provider" picker can use the SAME motion for its
//   connect offer rather than defining a near-copy: one slide, one duration, no drift.
export const ORG_SWAP_ANIMATION = {
  animation: 'hwOrgIn 400ms ease-out',
  '@keyframes hwOrgIn': {
    from: { opacity: 0, transform: 'translateX(200px)' },
    to: { opacity: 1, transform: 'none' },
  },
} as const;

// SINGLE source of truth for the AI chart-summary disclaimer — used everywhere an AI summary renders (ValueChart,
// Trends detail) so the wording can never drift out of compliance in one copy. Aligned to the DiMe/CMS AI-companion
// pledge: clearly distinguishes EDUCATIONAL content from clinical guidance + guides to a health professional when needed.
export const AI_SUMMARY_DISCLAIMER =
  "An AI-generated educational summary to help you understand your own health data — not medical advice, a diagnosis, or clinical guidance. If anything here concerns you, or you're weighing a decision about your care, please talk with a healthcare professional.";

// Chat-appropriate sibling for the conversational assistant (same DiMe/CMS pledge framing: educational, not clinical
// guidance + guide to a professional when needed). Markdown (⚠️ + italics) since it renders as a chat message.
export const AI_CHAT_DISCLAIMER =
  "⚠️ *This assistant offers educational information to help you understand your own health records — not medical advice, a diagnosis, or clinical guidance. For decisions about your care, or anything that concerns you, please talk with a healthcare professional.*";

// --- Types ---
// One ready-to-render detail row. Built SERVER-SIDE by get_patient_resource_events (health_wallet.sql):
// the event's `details` payload is { primary: DetailEntry[], secondary: DetailEntry[] } — labels, date
// formatting and field selection all live in the SQL (single source of truth; the old FE
// extractDetailSummary was deleted 2026-07-12). Add a field to the detail view ⇒ edit the SQL VALUES list.
export interface DetailEntry {
  label: string;
  value: string;
}

// Panel-level interpretation attached to an observation reading/group — the containing DiagnosticReport's
// conclusion (Normal/Abnormal) + panel name. Emitted by get_patient_resource (latest reading, per card) and
// get_patient_resource_events (per reading). Rendered SECONDARY to the analyte's own interpretation.
export interface PanelLabel {
  name?: string | null;
  interpretation?: string | null;
}

// Analyte-level interpretation — the source's OWN flag on the individual result (Observation.interpretation:
// H/L/A/N/HH/LL…), from the C-CDA inline <interpretationCode> (1a) or Epic-native. PRIMARY signal, rendered
// ABOVE the panel label. DISTINCT from — and never feeds — the range-derived Trends/sparkline coloring.
export interface Interpretation {
  code?: string | null;    // HL7 ObservationInterpretation code (H/L/A/N/HH/LL/…)
  display?: string | null; // human text ("High", "Low", "Abnormal", "Normal") when the source provides it
}

export interface GroupedSummary {
  name: string;
  // Analyte identity key (observations, Option 2 code-keyed cards): COALESCE(code, name). The drill-down filters
  // on THIS, not the display `name`, so same-LOINC/different-label readings resolve to one card + one drill-down.
  // Absent for non-observation resources and the nameless "other" bucket (→ '' = nameless).
  key?: string | null;
  // Conditions (Option 2, code-deduped/name-grouped): the DISTINCT standard codes ("canon|code") rolled into this
  // name-card = the card's IDENTITY. Surfaced on the card (provenance) AND what the detail/visit-trail scopes by
  // (code_std = ANY(codes)) — not the display name. Absent for other resources.
  codes?: string[] | null;
  count: number;
  firstDate: string;
  lastDate: string;
  hasChart?: boolean;
  resource?: string;
  // Dominant clinicalStatus per group (Conditions; get_patient_resource 2026-07-12) — feeds
  // RendererV1's Current/Past classification. null/absent = status-less (recency fallback).
  status?: string | null;
  // Symptom-vs-diagnosis kind (conditions; SNOMED semantic tag via name suffix or snomed_codes).
  // 'symptom' routes the group to the Symptoms bucket; null/absent = unclassified (Current/Past).
  kind?: string | null;
  // Medication card fields (medications slice only; jsonb_strip_nulls drops absent ones).
  dose?: string | null;        // dominant dosageInstruction/dosage text
  route?: string | null;       // dominant route display (subtitle; regex FALLBACK for Infusions)
  routeCode?: string | null;   // dominant route CODE (SNOMED/NCI) — PRIMARY Infusions signal
  indication?: string | null;  // dominant reasonCode ("For <indication>" sub-headers)
  aka?: string[];              // alternate source names merged into this card (brands/spelling variants) — rendered as "Name (aka…)"
  // Procedure card fields (procedures slice only): code drives the CPT-range category classifier.
  code?: string | null;
  codeSystem?: string | null;
  org?: string | null;
  // Observation card fields (observations slice only): FHIR category drives the section classifier;
  // latest* = the group's most recent value (numeric+unit, or categorical text like "Never smoker").
  category?: string | null;
  latestValue?: number | null;
  latestUnit?: string | null;
  latestText?: string | null;
  interpretation?: Interpretation | null; // latest reading's OWN flag — PRIMARY, shown above the panel label
  panel?: PanelLabel | null; // latest reading's panel interp (2b) — shown SECONDARY to the analyte's own flag
  // Planned/upcoming classification (medications/procedures; get_patient_resource). Both optional.
  mood?: string;               // raw CDA moodCode (INT/RQO/PRP/PRMS)
  intent?: string;             // FHIR MedicationRequest/ServiceRequest.intent
}

// --- Helpers ---
export function formatDate(dateString: string) {
  if (!dateString) return 'N/A';
  // Date-only strings parse as UTC midnight — render them in UTC or the calendar
  // date shifts back a day in western timezones (May 31 → "May 30").
  const opts: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) opts.timeZone = 'UTC';
  return new Date(dateString).toLocaleDateString('en-US', opts);
}

// Panel interpretation — a full plain-language sentence (Leo 2026-07-25) so it reads as an explanation, not a
// field, and is unmistakably the PANEL's (distinct from the analyte's own bare flag "Low"/"High"). Returns the
// sentence LEAD and the interpretation WORD separately so the render can BOLD just the word and keep the whole
// line NEUTRAL grey (Leo 2026-07-26: the panel interp is NOT the color signal — the analyte's own flag is).
// "This test was part of the {name} panel, which was interpreted as " + **{interp}**. We only append " panel"
// when the name doesn't already contain it (avoid "…panel panel"); no name → "…part of a panel that was
// interpreted as ". Blank/absent interpretation → null.
export function formatPanelLabel(panel?: PanelLabel | null): { lead: string; interp: string } | null {
  const interp = panel?.interpretation?.trim();
  if (!interp) return null;
  const name = panel?.name?.trim();
  const named = name ? (/panel/i.test(name) ? name : `${name} panel`) : null;
  const lead = named
    ? `This test was part of the ${named}, which was interpreted as `
    : 'This test was part of a panel that was interpreted as ';
  return { lead, interp };
}

// Analyte-flag display: prefer the source's own text; else map the common ObservationInterpretation codes;
// else the raw code. Null when there's no flag at all.
const INTERP_CODE_LABEL: Record<string, string> = {
  N: 'Normal', A: 'Abnormal', AA: 'Critical', H: 'High', L: 'Low', HH: 'Critical high', LL: 'Critical low',
  HU: 'Very high', LU: 'Very low', I: 'Intermediate', R: 'Resistant', S: 'Susceptible',
  POS: 'Positive', NEG: 'Negative', DET: 'Detected', ND: 'Not detected',
};
export function formatInterpretation(interp?: Interpretation | null): string | null {
  const disp = interp?.display?.trim();
  if (disp) return disp;
  const code = interp?.code?.trim();
  if (!code) return null;
  return INTERP_CODE_LABEL[code.toUpperCase()] ?? code;
}

// Abnormal = any flag that isn't an explicit Normal. Unknown/other codes read as abnormal so a real flag is
// never shown as benign; drives the amber vs muted color of the analyte chip.
export function isAbnormalInterp(interp?: Interpretation | null): boolean {
  const code = interp?.code?.trim().toUpperCase();
  if (code) return code !== 'N';
  const disp = interp?.display?.trim();
  return !!disp && !/^normal$/i.test(disp);
}

// Color for the PANEL-conclusion word ("…interpreted as **Abnormal**"). The panel is a SECONDARY signal (the
// analyte's OWN flag is primary and uses the intense #bd352b/#1a7f3c), so this is a MUTED palette that sits
// visually below it. Reuses isAbnormalInterp on the free-text conclusion (abnormal = anything but Normal).
export function panelInterpColor(interp?: string | null): string {
  if (!interp?.trim()) return '#8e8e93';
  return isAbnormalInterp({ display: interp }) ? '#c0736b' /* muted red */ : '#6b9e7c' /* muted green */;
}

// Count semantics for the "Your record at a glance" hero + the "why so many records" box
// (see project_hw_v1_hero_pill_unify). X = deduped RECORD volume (totalRecords); Y = GROUP/card count
// (named + other = the tile sum); D = duplicate copies removed. `grouped` gates BOTH the hero's
// "records → grouped into Y" volume clause and the explainer box: true only when records exceed groups
// (something was documented more than once). X = Y ⇒ each item appears once → show only the Y count.
export interface RecordStats { x: number; y: number; d: number; grouped: boolean; }
export function recordStats(totalRecords: number, groupCount: number, rawRecords: number): RecordStats {
  const x = totalRecords;
  return { x, y: groupCount, d: Math.max(0, rawRecords - x), grouped: x > groupCount };
}

// (extractDetailSummary DELETED 2026-07-12 — its field-picking/labels/date-formatting moved into
// get_patient_resource_events' SQL, which now ships ready-to-render {primary, secondary} DetailEntry rows.)

// --- Chart Helpers ---
export function computeYDomain(values: number[]): [number, number] {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const buffer = range * 0.15;
  const rawMin = min - buffer;
  const rawMax = max + buffer;
  // Round to clean step: pick a step size based on range magnitude
  const magnitude = Math.pow(10, Math.floor(Math.log10(range || 1)));
  const step = magnitude <= 0 ? 1 : range / magnitude > 5 ? magnitude * 2 : magnitude;
  // Use parseFloat + toPrecision to eliminate floating point artifacts
  const decimals = Math.max(0, -Math.floor(Math.log10(step))) + 1;
  const cleanMin = parseFloat((Math.floor(rawMin / step) * step).toFixed(decimals));
  const cleanMax = parseFloat((Math.ceil(rawMax / step) * step).toFixed(decimals));
  return [cleanMin, cleanMax];
}

export function formatYTick(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 100) return value.toFixed(0);
  if (abs >= 1) return value.toFixed(1);
  if (abs >= 0.1) return value.toFixed(2);
  return value.toFixed(3);
}
